/**
 * TypeScript mirror of the HTTP contract documented in `docs/API.md` (v1).
 *
 * This file is the only place the wire format is described; nothing else in
 * the frontend re-declares a payload shape. Changing the contract means
 * changing `docs/API.md` first, then this file.
 *
 * Field names are the wire's own `snake_case`, untouched. `null` is meaningful
 * everywhere it appears: a measured fact the console could not take is `null`
 * with its own reason string, never a zero, never a `false`, and never a value
 * carried over from somewhere else.
 */

// ------------------------------------------------------------------- errors

/** The one error envelope every non-2xx answer carries. */
export interface ApiErrorBody {
  error: string;
  message: string;
  details?: Record<string, unknown> | null;
}

/**
 * The four states a version can be in when its bytes are not readable.
 *
 * One slug (`version_not_available`) covers all four because from the caller's
 * side one thing failed; `details.state` says which, because "not yet" and
 * "never again" are not the same news.
 */
export type UnavailableState =
  | "awaiting_bytes"
  | "verifying"
  | "rejected"
  | "deleted";

// --------------------------------------------------------------------- auth

/** How this deployment signs people in. */
export type AuthMode = "google" | "dev";

export interface AuthConfig {
  mode: AuthMode;
  /** Where a sign-in starts; `null` in the server's development mode. */
  sign_in_url: string | null;
  /** The e-mail domain the server accepts; `null` in development mode. */
  domain: string | null;
  /** Who a new person is told to ask; `null` when the deployment names nobody. */
  access_contact: string | null;
}

// ------------------------------------------------------------------ account

/** Signing in is not access: a new account is `pending` until approved. */
export type AccountState = "pending" | "active" | "disabled";

/**
 * The console's own code — never called "MFA" on any screen.
 *
 * `confirmed_at` is when this session last proved a code and `expires_at` is
 * `confirmed_at + step_up_minutes`; both are `null` when the window is shut.
 */
export interface SecondFactor {
  enrolled: boolean;
  enrolled_at: string | null;
  confirmed_at: string | null;
  expires_at: string | null;
}

export interface Me {
  username: string;
  email: string;
  display_name: string | null;
  state: AccountState;
  is_admin: boolean;
  second_factor: SecondFactor;
  /** The operator's emergency stop — a console fact, never a personal allowance. */
  console_paused: boolean;
  disabled_reason: string | null;
}

// There is deliberately no `limits` block on `Me`, and nothing personal to put
// in one: no per-person storage budget, no per-person upload count, no
// per-person read count. What a form may offer comes from `GET /config`, which
// the server enforces (`docs/API.md`, *There is no `limits` block*).

/** The one field on this surface a person owns. */
export interface MeUpdate {
  display_name: string | null;
}

/** The only response that ever carries the secret or the recovery codes. */
export interface SecondFactorEnrolment {
  otpauth_uri: string;
  /** Drawn by the server, inline: no client library ever sees the secret. */
  qr_svg: string;
  /** Ten codes, each usable once, shown here and never again. */
  recovery_codes: string[];
  confirmed: boolean;
}

export interface SecondFactorCode {
  code: string;
}

export interface SecondFactorConfirmed {
  confirmed_at: string;
  expires_at: string;
  recovery_codes_left: number;
}

// -------------------------------------------------------------- console keys

export interface ConsoleKeyCreate {
  name: string;
  /**
   * The key's blast radius, and it is required.
   *
   * Account slugs, or the explicit `["*"]` meaning every account including
   * ones created after this key. An omitted list would mean "everything",
   * which is precisely the default this removes.
   */
  accounts: string[];
  /** `null` (no expiry), 30 or 90. */
  expires_days: number | null;
}

export interface ConsoleKey {
  id: string;
  name: string;
  prefix: string;
  accounts: string[];
  all_accounts: boolean;
  created_at: string;
  /** Written at a resolution of one minute; never a live indicator. */
  last_used_at: string | null;
  expires_at: string | null;
}

/** `key` appears in this response and in no other, ever. */
export interface CreatedConsoleKey extends ConsoleKey {
  key: string;
}

export interface ConsoleKeyList {
  items: ConsoleKey[];
}

/** The explicit "every account, including future ones" scope. */
export const ALL_ACCOUNTS_SCOPE = "*";

// ------------------------------------------------------------------- config

/** The whole vocabulary of permission in this product. Three words. */
export type Purpose = "benchmark" | "training" | "exploration";

/** The canonical order, used wherever purposes are listed or defaulted. */
export const PURPOSES: readonly Purpose[] = [
  "benchmark",
  "training",
  "exploration",
];

/**
 * What kind of thing a consent's source is. Five, and one cannot be typed.
 *
 * `note` is not a slur: it is the honest name for free text a colleague wrote
 * down, it stays permanently valid, and the console never scores these. The
 * order here is the wire's vocabulary and not a ranking; what a form offers is
 * `evidence_kinds` from `GET /config` and nothing else (ADR-0013, ADR-0016).
 */
export type EvidenceKind = "dpa" | "nda" | "email" | "client_attestation" | "note";

/** One published kind: the two strings the product prints, and who may write it. */
export interface EvidenceKindOption {
  kind: EvidenceKind;
  /** What a picker shows. */
  label: string;
  /** What a refusal and a ledger row read out, inside the sentence. */
  in_a_sentence: string;
  /** `false` means a form may not offer it: the console writes it itself. */
  offerable: boolean;
  /** Why it is not offerable; `null` when it is. */
  reserved_reason: string | null;
}

/**
 * What evidence may grant training, published as a rule with its reason.
 *
 * `kinds: []` is the shipped state and means the rule is off: any kind may
 * grant training. The server is the enforcer either way — a form reads this so
 * it can say so before somebody types, rather than after.
 */
export interface TrainingEvidenceRule {
  kinds: EvidenceKind[];
  reason: string;
}

export type TaskType = "classification" | "regression" | "time_series";

export type FileFormat = "csv" | "parquet";

/** A property of the metric, published by the server and never typed by a person. */
export type MetricDirection = "higher_is_better" | "lower_is_better";

export interface MetricOption {
  metric: string;
  label: string;
  direction: MetricDirection;
}

export interface AccessTtlConfig {
  default: number;
  max: number;
}

export interface RetentionConfig {
  /** `null` means "kept until a client asks"; the reason says so out loud. */
  default_days: number | null;
  reason: string | null;
}

export interface ScanConfig {
  performed: boolean;
  reason: string | null;
}

export interface EvaluationsConfig {
  run_url_required: boolean;
  /** `null` when no tracking-server address is configured. */
  run_url_prefix: string | null;
  run_url_prefix_reason: string | null;
}

/**
 * What the console declares about sign-in, and what it refuses to claim.
 *
 * `measured: false` is not a hedge, it is the fact: a Google ID token carries
 * no proof that a second factor was used.
 */
export interface SsoConfig {
  two_step_enforced_by: string;
  measured: boolean;
  note: string;
}

export interface StorageReplica {
  region: string;
  description: string;
}

export interface StorageReplication {
  /** Measured: the age of the oldest available version with no confirmed copy. */
  lag_seconds: number | null;
  measured: boolean;
  reason: string | null;
  /** Declared: what the copying job was configured to do. */
  interval_minutes: number;
  alert_seconds: number;
}

export interface StorageConfig {
  region: string | null;
  description: string | null;
  /** `null` when no second region is configured, with the reason beside it. */
  replica: StorageReplica | null;
  replica_reason: string | null;
  replication: StorageReplication | null;
}

/** What forms may pre-fill; every entry is empty or the safe end. */
export interface ConfigDefaults {
  /** Always `[]`: nothing about a client's data is ever pre-ticked. */
  purposes: Purpose[];
  task_type: TaskType | null;
  access_ttl_seconds: number;
  deletion_immediate: boolean;
}

/** The endpoint that exists so no client ever invents a number. */
export interface ConsoleConfig {
  purposes: Purpose[];
  /** The whole list, published so no form invents a kind of its own. */
  evidence_kinds: EvidenceKindOption[];
  training_requires_evidence_kind: TrainingEvidenceRule;
  task_types: TaskType[];
  formats: FileFormat[];
  metrics: Record<TaskType, MetricOption[]>;
  max_upload_gb: number;
  part_mb: number;
  upload_url_ttl_seconds: number;
  upload_deadline_minutes: number;
  access_ttl: AccessTtlConfig;
  step_up_minutes: number;
  max_console_keys: number;
  access_grants_per_key_per_hour: number;
  quarantine_days: number;
  deletion_grace_hours: number;
  max_stored_tb: number;
  retention: RetentionConfig;
  scan: ScanConfig;
  evaluations: EvaluationsConfig;
  sso: SsoConfig;
  storage: StorageConfig;
  paused: boolean;
  access_contact: string | null;
  defaults: ConfigDefaults;
}

// ----------------------------------------------------------------- accounts

/** A client company: what a contract is with, and what a consent belongs to. */
export interface Account {
  id: string;
  name: string;
  /** Frozen at creation: it is a path segment in every file this client gave us. */
  slug: string;
  created_by: string;
  created_at: string;
  /** Counts the screen needs; deleted datasets and unavailable versions excluded. */
  datasets: number;
  versions_available: number;
}

export interface AccountList {
  items: Account[];
  next_after: string | null;
}

export interface AccountCreate {
  name: string;
  slug: string;
}

export interface AccountListQuery {
  after?: string;
  limit?: number;
}

// ------------------------------------------------------------------ consent

export interface Consent {
  id: string;
  permissions: Purpose[];
  /** Required in both directions: a consent with no source is somebody's memory. */
  evidence: string;
  /** What kind of thing that source is. Every row carries it, superseded ones too. */
  evidence_kind: EvidenceKind;
  /** The thing somebody can go and look at; `null` is an honest absence. */
  evidence_ref: string | null;
  /**
   * The reference as an address, when it is one — derived by the server.
   *
   * `null` means "not an address", never "wrong". A screen that renders it
   * offers fixed words, never the address itself, and claims nothing about
   * what is behind it (ADR-0017).
   */
  evidence_url: string | null;
  /**
   * The console's own sentence fragment: the kind in words, then the reference.
   *
   * Composed on the side that knows the wording and **rendered verbatim**. A
   * screen that re-composed it from `evidence_kind` and `evidence_ref` would
   * be a second wording of the same fact, and the two drift.
   */
  source: string;
  recorded_by: string;
  at: string;
  superseded_at: string | null;
  /**
   * The console's own sentences, present on the answer to a change.
   *
   * Rendered verbatim and in order — the third one is the honest window about
   * links already issued.
   */
  lines?: string[];
}

export interface ConsentList {
  items: Consent[];
}

export interface ConsentCreate {
  /** The whole new list, every time. Not a diff, not an add, not a remove. */
  permissions: Purpose[];
  evidence: string;
  /** Required in both directions; `client_attestation` is refused on any manual write. */
  evidence_kind: EvidenceKind;
  /** Optional in both directions: `null` rather than a typed "n/a". */
  evidence_ref: string | null;
}

// --------------------------------------------------------------- deal gate

export type GateState = "no_bar" | "bar_set" | "bar_cleared" | "not_yet_passed";

export interface GateBest {
  value: number;
  /** Signed in the direction the metric runs. */
  margin: number;
  version_number: number;
  recorded_by: string;
  recorded_at: string;
  run_url: string;
}

/** Derived on every read and stored nowhere. It is never `failed`. */
export interface Gate {
  state: GateState;
  metric: string | null;
  direction: MetricDirection | null;
  threshold_value: number | null;
  /** What the client's own model scores. A different number from the threshold. */
  baseline_value: number | null;
  /** Why there is no baseline; an invented one is the worst number in the building. */
  baseline_absent_reason: string | null;
  set_by: string | null;
  evidence: string | null;
  at: string | null;
  best: GateBest | null;
  /** Every evaluation recorded, including the ones that did not clear the bar. */
  attempts: number;
}

export interface TargetCreate {
  metric: string;
  threshold_value: number;
  baseline_value: number | null;
  baseline_absent_reason: string | null;
  evidence: string;
}

export interface Target {
  id: string;
  metric: string;
  direction: MetricDirection;
  threshold_value: number;
  baseline_value: number | null;
  baseline_absent_reason: string | null;
  evidence: string;
  set_by: string;
  at: string;
  superseded_at: string | null;
}

export interface TargetList {
  items: Target[];
}

export interface EvaluationCreate {
  version_number: number;
  metric: string;
  value: number;
  /** Required: a result with no run behind it is a claim, not a measurement. */
  run_url: string;
  notes: string | null;
}

export interface Evaluation {
  id: string;
  version_number: number;
  /** `null` when the metric is not the bar's own — a real measurement of something else. */
  target_id: string | null;
  metric: string;
  value: number;
  /** Computed, never entered; `null` when there is no bar to pass. */
  passed: boolean | null;
  passed_reason: string | null;
  threshold_value: number | null;
  baseline_value: number | null;
  /** `"reported"` and nothing else: the console did not run this. */
  measured_by: "reported";
  run_url: string;
  recorded_by: string;
  recorded_at: string;
  notes: string | null;
}

export interface EvaluationList {
  items: Evaluation[];
}

// ------------------------------------------------------------------- events

/** The eleven kinds. A twelfth is expensive: a count is a message, not a state. */
export type EventKind =
  | "created"
  | "bytes_received"
  | "verifying"
  | "available"
  | "rejected"
  | "replicated"
  | "corrected"
  | "consent_changed"
  | "bar_set"
  | "evaluation_recorded"
  | "deleted";

/** One line of the trace: the product explaining itself, rendered verbatim. */
export interface DatasetEvent {
  /** Stable, counting from 0 in the order the console wrote them. */
  index: number;
  at: string;
  /** `null` on events belonging to the dataset itself rather than to a version. */
  version_number?: number | null;
  kind: EventKind;
  message: string;
}

export interface DatasetEventList {
  items: DatasetEvent[];
  next_after: number | null;
}

// ----------------------------------------------------------------- datasets

export interface DatasetAccountRef {
  id: string;
  name: string;
  slug: string;
}

export interface VersionSummary {
  number: number;
  state: VersionState;
  size_bytes: number | null;
  available_at: string | null;
}

export interface DatasetVersions {
  total: number;
  available: number;
  latest: VersionSummary | null;
}

export interface Dataset {
  id: string;
  account: DatasetAccountRef;
  name: string;
  slug: string;
  task_type: TaskType;
  /** Declared, and correctable; whether it is in the file is measured per version. */
  target_column: string | null;
  owner: string;
  created_at: string;
  deleted_at: string | null;
  deleted_by: string | null;
  /** The consent in force: always the newest non-superseded row. */
  consent: Consent;
  gate: Gate;
  versions: DatasetVersions;
  deletion: DeletionRequest | null;
  events: DatasetEvent[];
}

export interface DatasetList {
  items: Dataset[];
  /**
   * The accounts a `dk_` key was minted for, when a key narrowed this list.
   *
   * Absent for a browser session, which reads the whole catalogue. It is on
   * the payload so a script never mistakes a narrowed list for a shrinking
   * catalogue.
   */
  scope?: string[] | null;
  next_after?: string | null;
}

export interface DatasetConsentCreate {
  permissions: Purpose[];
  evidence: string;
  /** Required beside the evidence; `client_attestation` is refused here too. */
  evidence_kind: EvidenceKind;
  evidence_ref: string | null;
}

export interface DatasetCreate {
  account_id: string;
  name: string;
  task_type: TaskType;
  target_column: string | null;
  /** Required, and its permissions may not be empty. */
  consent: DatasetConsentCreate;
  /** Optional: "Not yet — the client has not told me" is a real answer. */
  target?: TargetCreate | null;
}

export interface DatasetUpdate {
  name?: string;
  target_column?: string | null;
  task_type?: TaskType;
}

export interface DatasetListQuery {
  account?: string;
  after?: string;
  limit?: number;
}

// ----------------------------------------------------------------- versions

/** Five states, and a sixth would be expensive. */
export type VersionState =
  | "awaiting_bytes"
  | "verifying"
  | "available"
  | "rejected"
  | "deleted";

/** The states a version leaves on its own; a screen showing one keeps asking. */
export const MOVING_VERSION_STATES: readonly VersionState[] = [
  "awaiting_bytes",
  "verifying",
];

/** The states nothing moves out of. */
export const TERMINAL_VERSION_STATES: readonly VersionState[] = [
  "rejected",
  "deleted",
];

/** Exactly three values, and each has its own sentence on the screen. */
export type RejectedReason =
  | "upload_never_arrived"
  | "unreadable_file"
  | "malware_found";

/** How the second copy's identity was checked, when there is one. */
export type ReplicaCheck = "etag_md5" | "size_only";

export interface SchemaColumn {
  name: string;
  type: string;
  null_count: number | null;
}

export interface ConsentAtUpload {
  permissions: Purpose[];
  evidence: string;
  evidence_kind: EvidenceKind;
  evidence_ref: string | null;
  /** The reference as an address, when it is one — the ledger's rule (ADR-0017). */
  evidence_url: string | null;
  /** The console's own fragment, rendered verbatim like every other `source`. */
  source: string;
  at: string;
}

export interface UploadPart {
  part_number: number;
  first_byte: number;
  last_byte: number;
  /**
   * A presigned URL for one part. It is a credential with a clock: it is used
   * and never logged, never put in a message and never shown on a screen.
   */
  url: string;
}

export interface UploadPlan {
  upload_id: string;
  part_size_bytes: number;
  parts: UploadPart[];
  urls_expire_at: string;
  deadline_at: string;
}

export interface Version {
  number: number;
  dataset_id: string;
  state: VersionState;
  filename: string;
  format: FileFormat;
  /** Measured: what arrived, not what was declared. */
  size_bytes: number | null;
  checksum_sha256: string | null;
  /** Kept only so the second copy can be confirmed; never an integrity claim. */
  checksum_md5: string | null;
  /** Exact, never sampled. `null` with a reason when it could not be taken. */
  row_count: number | null;
  row_count_reason: string | null;
  column_count: number | null;
  /** Names, types and counts. Never a value and never a row. */
  schema: SchemaColumn[] | null;
  declared: { target_column: string | null };
  /** Measured: whether the declared column is actually in this file. */
  target_column_present: boolean | null;
  consent_id: string | null;
  /** The consent frozen at upload — history, never a licence. */
  consent_at_upload: ConsentAtUpload | null;
  uploaded_by: string;
  uploaded_via: "session" | "key";
  /** Server-authored provenance sentence, printed verbatim. */
  attribution: string;
  created_at: string;
  available_at: string | null;
  replicated_at: string | null;
  replica_check: ReplicaCheck | null;
  replication_reason: string | null;
  /**
   * When the verify pass handed every byte to the scanner and it answered
   * clean. `null` on a console with no scanner, and on versions stored
   * before it had one (ADR-0018).
   */
  scanned_at: string | null;
  /** The engine and its signature date; travels with `scanned_at`. */
  scanned_with: string | null;
  scan: ScanConfig;
  rejected_reason: RejectedReason | null;
  deleted_at: string | null;
  /** Present only on the answer that opens an upload. */
  upload?: UploadPlan;
  /** Present only on the answer that opens an upload: the version's first line. */
  events?: DatasetEvent[];
}

export interface VersionList {
  items: Version[];
  next_after?: number | null;
}

export interface VersionCreate {
  filename: string;
  format: FileFormat;
  size_bytes: number;
}

export interface CompletedPart {
  part_number: number;
  etag: string;
}

export interface VersionComplete {
  upload_id: string;
  parts: CompletedPart[];
}

// ------------------------------------------------------ reading a version

export interface AccessRequest {
  /** Required. The console will not guess why you are reading a client's data. */
  purpose: Purpose;
  note?: string | null;
  ttl_seconds?: number | null;
}

/**
 * A link to one object, for a short time.
 *
 * `url` is a bearer capability with a clock. It is handed to the person who
 * asked and it is never logged, never put in a message and never stored.
 */
export interface AccessGrant {
  url: string;
  expires_in: number;
  expires_at: string;
  access_id: string;
  filename: string;
  size_bytes: number;
  checksum_sha256: string;
}

/** The same decision, and nothing written. There is no `url` and no `access_id`. */
export interface AccessPreview {
  /** The console's own sentences, rendered verbatim and in order. */
  lines: string[];
  purpose: Purpose;
  permitted: Purpose[];
  expires_in: number;
  filename: string;
  size_bytes: number;
  checksum_sha256: string;
}

/** The Fetch block: written by the server, rendered exactly as sent. */
export interface FetchLines {
  lines: string[];
}

/** One row of the read ledger. Refusals are rows like any other. */
export interface Read {
  id: string;
  version_number: number;
  actor: string;
  via: "session" | "key";
  key_prefix: string | null;
  purpose: Purpose | null;
  note: string | null;
  granted: boolean;
  refusal_code: string | null;
  url_expires_at: string | null;
  at: string;
}

// There is deliberately no `downloaded` field on `Read`, not even a `null`
// one. The console issues a link; the bytes travel from storage to the reader
// without passing through it. A field that existed and was always `null` would
// invite a client to wait for the day it is filled in.

export interface ReadList {
  items: Read[];
  total: number;
  next_after: string | null;
}

// ----------------------------------------------------------------- deletion

export interface DeletionRequestCreate {
  reason: string;
  /** An admin act, for a client who asked for erasure now. */
  immediate: boolean;
}

export interface ReceiptObject {
  bucket: string;
  region: string;
  key: string;
  version_id: string;
  deleted_at: string;
  /** When the console asked for it again and was told it is gone. */
  absent_confirmed_at: string;
}

/** Written only after absence was verified in both regions. */
export interface Receipt {
  requested_by: string;
  requested_at: string;
  reason: string;
  immediate: boolean;
  completed_at: string;
  regions: string[];
  objects: ReceiptObject[];
  checksums_destroyed: string[];
  /** Printed verbatim; the last line is the uncomfortable one and is not optional. */
  lines: string[];
}

export interface DeletionRequest {
  id: string;
  requested_by: string;
  requested_at: string;
  scheduled_for: string;
  immediate: boolean;
  reason: string;
  completed_at: string | null;
  receipt: Receipt | null;
  /** The console's own sentences about what will happen, rendered verbatim. */
  lines: string[];
}

// -------------------------------------------------------------------- admin

export interface AdminUser {
  username: string;
  email: string;
  state: AccountState;
  is_admin: boolean;
  first_seen_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  disabled_reason: string | null;
  second_factor_enrolled: boolean;
  /** How many console keys can reach this account right now. */
  console_keys: number;
}

export interface AdminUserList {
  items: AdminUser[];
}

export interface DisableRequest {
  reason: string;
}

export interface AuditEvent {
  id: string;
  at: string;
  /** `null` for the acts nobody performed; the screen renders those as "console". */
  actor: string | null;
  subject: string | null;
  action: string;
  detail: Record<string, unknown> | null;
}

export interface AuditList {
  items: AuditEvent[];
  next_after: string | null;
}

export interface PauseRequest {
  paused: boolean;
  reason: string;
}

export interface PauseState {
  paused: boolean;
  reason: string | null;
  changed_by: string | null;
  changed_at: string | null;
}

export interface BucketHealth {
  name: string;
  region: string;
  objects: number | null;
  bytes: number | null;
  lifecycle_days?: number | null;
  versioning?: boolean | null;
  /** A bucket that could not be read keeps its entry and carries its own error. */
  error: string | null;
}

export interface StorageReplicationHealth {
  measured: boolean;
  reason: string | null;
  lag_seconds: number | null;
  unreplicated_versions: number | null;
  alert_seconds: number;
  alert: boolean;
  interval_minutes: number;
}

export interface VerifyHealth {
  queued: number;
  running: number;
  concurrency: number;
}

/** An object with no record behind it. A list, never an action. */
export interface Orphan {
  key: string;
  bytes: number;
  moved_at: string;
}

/**
 * A console key that tripped its hourly guard in the last day.
 *
 * The names are the wire's, not shorter ones. This is the one screen whose
 * whole purpose is naming a credential somebody may need to revoke, and a
 * field that is not in the payload renders as `undefined` on it — which `tsc`
 * cannot catch, because an interface can be wrong and self-consistent.
 */
export interface KeyOverVelocity {
  key_prefix: string;
  owner: string;
  /** Refusals, not links: these are the requests the guard turned away. */
  refusals: number;
  last_at: string;
}

export interface StorageHealth {
  buckets: {
    quarantine: BucketHealth | null;
    vault: BucketHealth | null;
    /** The whole object is `null` when no second region is configured. */
    replica: BucketHealth | null;
  };
  stored_bytes: number | null;
  max_stored_tb: number;
  replication: StorageReplicationHealth | null;
  verify: VerifyHealth;
  orphans: Orphan[];
  scan: ScanConfig;
  reconcile: { last_pass_at: string | null; stall_seconds: number };
  keys_over_velocity: KeyOverVelocity[];
}
